import { useMemo } from 'react'
import { useRouter } from 'next/router'

import { getQueryParamValueAsString, toQS } from '@/utils/url'
import { ROUTES, QUERY_PARAMS, INTERNAL_QUERY_PARAMS, SCOPE, OAUTH_CLIENTS, type ParamsObject } from '@/constants'

import useInvitation from './useInvitation'

type RouteKeys = keyof typeof ROUTES
type RoutePaths = (typeof ROUTES)[keyof typeof ROUTES]
type RouteObject = {
  url: (params?: Partial<ParamsObject>) => string
  path: RoutePaths
}
type Routes = Record<RouteKeys | 'returnToLogin', RouteObject>

export default function useRoutes() {
  const router = useRouter()
  const invitation = useInvitation()

  const value: Routes = useMemo(() => {
    const base: Routes = {} as Routes
    (Object.keys(ROUTES) as (keyof typeof ROUTES)[]).forEach((name) => {
      const path = ROUTES[name]
      base[name] = {
        url: (params) => {
          const withDefaultParams: ParamsObject = {} as ParamsObject

          Object.values(QUERY_PARAMS).forEach((paramName) => {
            withDefaultParams[paramName] = getQueryParamValueAsString(paramName, router.query)
          })

          if (withDefaultParams.callbackUrl) {
            withDefaultParams.callbackUrl = encodeURIComponent(withDefaultParams.callbackUrl)
          }

          withDefaultParams[INTERNAL_QUERY_PARAMS.rememberMe] = getQueryParamValueAsString(
            INTERNAL_QUERY_PARAMS.rememberMe,
            router.query,
          )
          return `${path}?${toQS({ ...withDefaultParams, ...params })}`
        },
        path,
      }
    })
    let returnToLogin = base.login
    const scope = getQueryParamValueAsString(QUERY_PARAMS.scope, router.query)
    if (scope === SCOPE.SCANSNAP) {
      returnToLogin = base.scanSnapAuthorization
    } else if (scope && OAUTH_CLIENTS.includes(scope)) {
      returnToLogin = base.oauthAuthorization
    } else if (invitation.type) {
      returnToLogin = base.invitationsLogin
    }

    base.returnToLogin = returnToLogin

    return base
  }, [router.query])

  return value
}
